<template>
  <div class="ncrm-workflow-detail">
    <v-card class="ncrm-workflow-detail__header pa-4">
      <div class="d-flex flex-wrap align-center justify-space-between ga-3">
        <div class="min-w-0">
          <div class="d-flex align-center ga-2">
            <v-btn icon="mdi-arrow-left" variant="text" size="small" @click="$emit('back')" />
            <div class="text-h6 ncrm-text-strong ncrm-workflow-detail__title">
              {{ workflow.name }}
            </div>
          </div>
          <div class="text-body-2 text-medium-emphasis mt-1">
            {{ $t("ncrm.workflows.detail.subtitle") }}
          </div>
        </div>

        <div class="d-flex flex-wrap align-center ga-2">
          <v-btn color="primary" variant="tonal" @click="$emit('edit-workflow', workflow)">
            <v-icon start>mdi-pencil-outline</v-icon>
            {{ $t("ncrm.workflows.actions.editWorkflow") }}
          </v-btn>
          <v-btn color="secondary" variant="tonal" @click="$emit('open-external-api')">
            <v-icon start>mdi-open-in-new</v-icon>
            {{ $t("ncrm.workflows.actions.openExternalApi") }}
          </v-btn>
        </div>
      </div>

      <div class="d-flex flex-wrap ga-2 mt-4">
        <v-chip color="primary">
          {{ $t("ncrm.workflows.detail.template") }}: {{ templateName }}
        </v-chip>
        <v-chip color="info">
          {{ $t("ncrm.workflows.detail.apiSource") }}: {{ apiSummary.trigger }}
        </v-chip>
        <v-chip color="neutral">
          {{ $t("ncrm.workflows.detail.version") }}: {{ workflow.version }}
        </v-chip>
      </div>
    </v-card>

    <NcrmWorkflowNodeRail
      v-model:selected-node-key="selectedNodeKey"
      :nodes="nodes"
      :api-node-key="apiNodeKey"
      :duplicate-node-key="duplicateNodeKey"
      :data-enrichment-node-key="dataEnrichmentNodeKey"
      :crm-write-node-key="crmWriteNodeKey"
      :lead-board-node-key="leadBoardNodeKey"
      :api-summary="apiSummary"
      :auto-sync-settings="autoSyncSettings"
      :dedup-auto-run-settings="dedupAutoRunSettings"
      :enrichment-settings="enrichmentSettings"
      :crm-write-settings="crmWriteSettings"
      :lead-board-settings="leadBoardSettings"
      :auto-sync-label="autoSyncLabel"
      :dedup-auto-run-label="dedupAutoRunLabel"
      :enrichment-auto-label="enrichmentAutoLabel"
      :crm-write-auto-run-label="crmWriteAutoRunLabel"
      :lead-board-auto-run-label="leadBoardAutoRunLabel"
      @edit-node="$emit('edit-node', $event)"
    />

    <!-- ── Staging table — shown above deliveries for API Import node ──────── -->
    <v-card v-if="showStagingTable" class="mt-4 ncrm-workflow-detail__staging">
      <NcrmWorkflowStagingTable
        :records="stagingRecords"
        :loading="loadingStagingRecords"
        :title="stagingTableTitle"
        :subtitle="stagingTableSubtitle"
        :empty-text="$t('ncrm.workflows.staging.empty')"
        :source-kind-filter="stagingSourceKindFilter"
        :status-scope="stagingStatusScope"
        :row-secondary-action-label="rowSecondaryActionLabel"
        :row-secondary-action-enabled-statuses="rowSecondaryActionEnabledStatuses"
        :row-secondary-action-disabled="rowSecondaryActionDisabled"
        :row-secondary-action-color="rowSecondaryActionColor"
        @filter-change="stagingActiveFilter = $event"
        @select="$emit('open-staging-record', $event)"
        @row-secondary-action="handleRowSecondaryAction"
      >
        <template #actions>
          <template v-if="selectedNodeKey === apiNodeKey">
            <v-chip
              v-if="autoSyncSettings.autoSync"
              color="success"
            >
              <v-icon start size="14">mdi-sync</v-icon>
              {{ $t('ncrm.workflows.staging.autoSyncActive') }}
            </v-chip>
            <v-btn
              color="primary"
              variant="tonal"
              size="small"
              :loading="loadingStagingImport"
              @click="$emit('run-import')"
            >
              <v-icon start>mdi-download-outline</v-icon>
              {{ $t('ncrm.workflows.staging.actions.runImport') }}
            </v-btn>
          </template>
          <template v-else-if="selectedNodeKey === fileNodeKey">
            <v-btn
              color="primary"
              variant="tonal"
              size="small"
              @click="$emit('open-file-import')"
            >
              <v-icon start>mdi-cloud-upload-outline</v-icon>
              {{ $t('ncrm.workflows.staging.actions.uploadFile') }}
            </v-btn>
          </template>
          <template v-else-if="selectedNodeKey === duplicateNodeKey">
            <v-chip
              v-if="dedupAutoRunSettings.autoRun"
              color="warning"
            >
              <v-icon start size="14">mdi-sync</v-icon>
              {{ $t('ncrm.workflows.staging.autoDedupActive') }}
            </v-chip>
            <v-btn
              color="warning"
              variant="tonal"
              size="small"
              :loading="loadingStagingDedup"
              @click="$emit('run-dedup')"
            >
              <v-icon start>mdi-account-search-outline</v-icon>
              {{ $t('ncrm.workflows.staging.actions.runDedup') }}
            </v-btn>
          </template>
          <template v-else-if="selectedNodeKey === dataEnrichmentNodeKey">
            <v-chip
              v-if="enrichmentSettings.mode === 'automatic'"
              color="info"
            >
              <v-icon start size="14">mdi-auto-fix</v-icon>
              {{ $t('ncrm.workflows.staging.autoEnrichmentActive') }}
            </v-chip>
            <v-btn
              color="secondary"
              variant="tonal"
              size="small"
              @click="$emit('open-ai-workspace')"
            >
              <v-icon start>mdi-open-in-new</v-icon>
              {{ $t('ncrm.workflows.actions.openAiWorkspace') }}
            </v-btn>
            <v-btn
              color="info"
              variant="tonal"
              size="small"
              :loading="loadingStagingEnrichmentSync"
              @click="$emit('sync-enrichment', {
                status: stagingActiveFilter,
              })"
            >
              <v-icon start>mdi-refresh</v-icon>
              {{ $t('ncrm.workflows.staging.actions.syncEnrichment') }}
            </v-btn>
            <v-btn
              color="info"
              variant="tonal"
              size="small"
              :loading="loadingStagingEnrichment"
              :disabled="!enrichmentBatchRecordCount"
              @click="$emit('run-enrichment-batch', {
                status: stagingActiveFilter,
                count: enrichmentBatchRecordCount,
              })"
            >
              <v-icon start>mdi-auto-fix</v-icon>
              {{ $t('ncrm.workflows.staging.actions.runEnrichmentBatch') }}
            </v-btn>
          </template>
          <template v-else-if="selectedNodeKey === crmWriteNodeKey">
            <v-chip
              v-if="crmWriteSettings.autoRun"
              color="success"
            >
              <v-icon start size="14">mdi-sync</v-icon>
              {{ $t('ncrm.workflows.staging.autoCrmWriteActive') }}
            </v-chip>
            <v-btn
              color="success"
              variant="tonal"
              size="small"
              :loading="loadingStagingWrite"
              :disabled="!crmWriteApprovedRecordIds.length"
              @click="$emit('write-approved-records', {
                stagingIds: crmWriteApprovedRecordIds,
                count: crmWriteApprovedRecordIds.length,
              })"
            >
              <v-icon start>mdi-database-arrow-right-outline</v-icon>
              {{ $t('ncrm.workflows.staging.actions.writeApprovedBatch') }}
            </v-btn>
          </template>
          <template v-else-if="selectedNodeKey === leadBoardNodeKey">
            <v-chip
              v-if="leadBoardSettings.autoRun"
              color="success"
            >
              <v-icon start size="14">mdi-sync</v-icon>
              {{ $t('ncrm.workflows.staging.autoLeadBoardActive') }}
            </v-chip>
            <v-btn
              color="success"
              variant="tonal"
              size="small"
              :loading="loadingStagingLeadBoard"
              :disabled="!leadBoardWritableRecordIds.length || !leadBoardSettings.boardId || !leadBoardSettings.stageId"
              @click="$emit('lead-board-add-batch', {
                stagingIds: leadBoardWritableRecordIds,
                count: leadBoardWritableRecordIds.length,
              })"
            >
              <v-icon start>mdi-flag-plus-outline</v-icon>
              {{ $t('ncrm.workflows.staging.actions.leadBoardAddBatch') }}
            </v-btn>
          </template>
        </template>
      </NcrmWorkflowStagingTable>
    </v-card>

    <!-- ── Events table (Import din API deliveries) ──────────────────────────── -->
    <v-card class="mt-4 ncrm-workflow-detail__events">
      <div class="ncrm-workflow-detail__events-header">
        <div>
          <div class="text-subtitle-1 font-weight-semibold ncrm-text-strong">
            {{ selectedNode?.label }}
          </div>
          <div class="text-body-2 text-medium-emphasis">
            {{ eventsSubtitle }}
          </div>
        </div>
        <v-btn
          v-if="usesDeliveryEvents"
          color="primary"
          variant="text"
          :loading="loadingDeliveries"
          @click="$emit('refresh-deliveries')"
        >
          <v-icon start>mdi-refresh</v-icon>
          {{ $t("app.actions.refresh") }}
        </v-btn>
      </div>

      <v-data-table
        :headers="eventHeaders"
        :items="nodeEvents"
        item-key="id"
        density="comfortable"
        :loading="loadingDeliveries"
        :items-per-page="10"
        class="ncrm-workflow-detail__table"
        :row-props="usesDeliveryEvents ? () => ({ class: 'ncrm-workflow-detail__table-row--clickable' }) : undefined"
        @click:row="handleDeliveryRowClick"
      >
        <template #item.status="{ item }">
          <v-chip size="small" :color="statusMeta(item.status).color" :variant="statusMeta(item.status).variant">
            {{ item.status }}
          </v-chip>
        </template>
        <template #item.result="{ item }">
          <v-chip :color="item.result === 'warning' ? 'warning' : 'success'">
            {{ resultLabel(item.result) }}
          </v-chip>
        </template>
        <template #item.action>
          <v-btn color="primary" variant="text" size="small" @click="$emit('open-external-api')">
            {{ $t("ncrm.workflows.actions.openExternalApi") }}
          </v-btn>
        </template>
        <template #no-data>
          <v-alert type="info" class="ma-4">
            {{ emptyText }}
          </v-alert>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import { resolveExternalApiStatusMeta } from "@/Utile/ncrmExternalApi.mjs";
import {
  PUBLIC_EXTERNAL_FORM_SOURCE_SYSTEMS,
  WORKFLOW_NODE_KEYS,
  CRM_WRITE_STATUS_SCOPE,
  DATA_ENRICHMENT_STATUS_SCOPE,
  LEAD_BOARD_STATUS_SCOPE,
  buildWorkflowSummary,
  createWorkflowNodeDefinitions,
  mapExternalApiDeliveryToWorkflowEvent,
} from "@/Utile/ncrmWorkflows.mjs";
import NcrmWorkflowNodeRail from "@/components/ncrm/workflows/NcrmWorkflowNodeRail.vue";
import NcrmWorkflowStagingTable from "@/components/ncrm/workflows/NcrmWorkflowStagingTable.vue";
import { formatDisplayTime } from "@/Utile/displayFormat.mjs";

export default {
  name: "NcrmWorkflowDetail",
  components: {
    NcrmWorkflowNodeRail,
    NcrmWorkflowStagingTable,
  },
  props: {
    workflow: {
      type: Object,
      required: true,
    },
    endpoints: {
      type: Array,
      default: () => [],
    },
    deliveries: {
      type: Array,
      default: () => [],
    },
    loadingDeliveries: {
      type: Boolean,
      default: false,
    },
    templateName: {
      type: String,
      default: "",
    },
    stagingRecords: {
      type: Array,
      default: () => [],
    },
    loadingStagingRecords: {
      type: Boolean,
      default: false,
    },
    loadingStagingImport: {
      type: Boolean,
      default: false,
    },
    loadingStagingDedup: {
      type: Boolean,
      default: false,
    },
    loadingStagingEnrichment: {
      type: Boolean,
      default: false,
    },
    loadingStagingEnrichmentSync: {
      type: Boolean,
      default: false,
    },
    loadingStagingWrite: {
      type: Boolean,
      default: false,
    },
    loadingStagingLeadBoard: {
      type: Boolean,
      default: false,
    },
    autoSyncSettings: {
      type: Object,
      default: () => ({ autoSync: false, autoSyncIntervalMinutes: 15 }),
    },
    dedupAutoRunSettings: {
      type: Object,
      default: () => ({ autoRun: false, autoRunIntervalMinutes: 15 }),
    },
    enrichmentSettings: {
      type: Object,
      default: () => ({ mode: "manual", promptKey: "ncrm_company_enrichment", autoRunIntervalMinutes: 60 }),
    },
    crmWriteSettings: {
      type: Object,
      default: () => ({ autoRun: false, autoRunIntervalMinutes: 15 }),
    },
    leadBoardSettings: {
      type: Object,
      default: () => ({ autoRun: false, boardId: null, stageId: null }),
    },
    lastAutoSyncAt: {
      type: Date,
      default: null,
    },
    lastAutoDedupAt: {
      type: Date,
      default: null,
    },
  },
  emits: [
    "back", "edit-workflow", "edit-node", "refresh-deliveries", "open-external-api", "open-delivery",
    "open-staging-record", "run-import", "run-dedup", "open-file-import", "open-ai-workspace",
    "run-enrichment-batch", "run-enrichment-record", "sync-enrichment", "write-staging-record",
    "write-approved-records", "lead-board-add-record", "lead-board-add-batch",
  ],
  data() {
    return {
      selectedNodeKey: WORKFLOW_NODE_KEYS.API_IMPORT,
      stagingActiveFilter: "all",
    };
  },
  computed: {
    apiNodeKey() { return WORKFLOW_NODE_KEYS.API_IMPORT; },
    externalFormNodeKey() { return WORKFLOW_NODE_KEYS.EXTERNAL_FORM_WIDGET; },
    fileNodeKey() { return WORKFLOW_NODE_KEYS.FILE_IMPORT; },
    duplicateNodeKey() { return WORKFLOW_NODE_KEYS.DUPLICATE_CHECK; },
    dataEnrichmentNodeKey() { return WORKFLOW_NODE_KEYS.DATA_ENRICHMENT; },
    crmWriteNodeKey() { return WORKFLOW_NODE_KEYS.CRM_WRITE; },
    leadBoardNodeKey() { return WORKFLOW_NODE_KEYS.LEAD_BOARD_ADD; },
    nodes() {
      return createWorkflowNodeDefinitions(this.$t);
    },
    selectedNode() {
      return this.nodes.find((node) => node.key === this.selectedNodeKey) || this.nodes[0];
    },
    apiSummary() {
      return buildWorkflowSummary(this.workflow, this.endpoints);
    },
    eventHeaders() {
      return [
        { title: this.$t("ncrm.workflows.events.headers.receivedAt"), key: "receivedAt", sortable: false },
        { title: this.$t("ncrm.workflows.events.headers.externalId"), key: "externalId", sortable: false },
        { title: this.$t("ncrm.workflows.events.headers.sourceApi"), key: "sourceApi", sortable: false },
        { title: this.$t("ncrm.workflows.events.headers.entityType"), key: "entityType", sortable: false },
        { title: this.$t("ncrm.workflows.events.headers.status"), key: "status", sortable: false },
        { title: this.$t("ncrm.workflows.events.headers.result"), key: "result", sortable: false },
        { title: this.$t("ncrm.workflows.events.headers.warning"), key: "warning", sortable: false },
        { title: this.$t("ncrm.workflows.events.headers.action"), key: "action", sortable: false, width: 132 },
      ];
    },
    nodeEvents() {
      const locale = this.$i18n?.locale === "en" ? "en-US" : this.$i18n?.locale === "ru" ? "ru-RU" : "ro-RO";
      if (this.selectedNodeKey === this.externalFormNodeKey) {
        return this.deliveries
          .filter((delivery) => PUBLIC_EXTERNAL_FORM_SOURCE_SYSTEMS.includes(delivery.source_system))
          .map((delivery) => mapExternalApiDeliveryToWorkflowEvent(delivery, this.apiSummary.endpoint || {}, locale));
      }
      if (this.selectedNodeKey === this.apiNodeKey) {
        return this.deliveries
          .filter((delivery) => !PUBLIC_EXTERNAL_FORM_SOURCE_SYSTEMS.includes(delivery.source_system))
          .map((delivery) => mapExternalApiDeliveryToWorkflowEvent(delivery, this.apiSummary.endpoint || {}, locale));
      }
      return [];
    },
    eventsSubtitle() {
      const keyByNode = {
        [this.externalFormNodeKey]: "externalFormsSubtitle",
        [this.apiNodeKey]: "apiSubtitle",
        [this.duplicateNodeKey]: "dedupSubtitle",
        [this.dataEnrichmentNodeKey]: "enrichmentSubtitle",
        [this.crmWriteNodeKey]: "crmWriteSubtitle",
        [this.leadBoardNodeKey]: "leadBoardSubtitle",
      };
      return this.$t(`ncrm.workflows.events.${keyByNode[this.selectedNodeKey] || "futureSubtitle"}`);
    },
    autoSyncLabel() {
      const mins = this.autoSyncSettings?.autoSyncIntervalMinutes ?? 15;
      if (mins < 60) return this.$t("ncrm.workflows.nodeSettings.autoSyncChipMin", { n: mins });
      return this.$t("ncrm.workflows.nodeSettings.autoSyncChipHour", { n: Math.round(mins / 60) });
    },
    dedupAutoRunLabel() {
      const mins = this.dedupAutoRunSettings?.autoRunIntervalMinutes ?? 15; if (Number(mins) === 0) return this.$t("ncrm.workflows.nodeSettings.autoSyncChipImmediate");
      if (mins < 60) return this.$t("ncrm.workflows.nodeSettings.autoSyncChipMin", { n: mins });
      return this.$t("ncrm.workflows.nodeSettings.autoSyncChipHour", { n: Math.round(mins / 60) });
    },
    enrichmentAutoLabel() {
      const mins = this.enrichmentSettings?.autoRunIntervalMinutes ?? 60;
      if (mins < 60) return this.$t("ncrm.workflows.nodeSettings.autoSyncChipMin", { n: mins });
      return this.$t("ncrm.workflows.nodeSettings.autoSyncChipHour", { n: Math.round(mins / 60) });
    },
    crmWriteAutoRunLabel() {
      const mins = this.crmWriteSettings?.autoRunIntervalMinutes ?? 15;
      if (mins < 60) return this.$t("ncrm.workflows.nodeSettings.autoSyncChipMin", { n: mins });
      return this.$t("ncrm.workflows.nodeSettings.autoSyncChipHour", { n: Math.round(mins / 60) });
    },
    leadBoardAutoRunLabel() {
      const mins = this.leadBoardSettings?.autoRunIntervalMinutes ?? 15;
      if (mins < 60) return this.$t("ncrm.workflows.nodeSettings.autoSyncChipMin", { n: mins });
      return this.$t("ncrm.workflows.nodeSettings.autoSyncChipHour", { n: Math.round(mins / 60) });
    },
    usesDeliveryEvents() {
      return [this.apiNodeKey, this.externalFormNodeKey].includes(this.selectedNodeKey);
    },
    showStagingTable() {
      return [
        this.apiNodeKey,
        this.fileNodeKey,
        this.duplicateNodeKey,
        this.dataEnrichmentNodeKey,
        this.crmWriteNodeKey,
        this.leadBoardNodeKey,
      ].includes(this.selectedNodeKey);
    },
    stagingSourceKindFilter() {
      if (this.selectedNodeKey === this.apiNodeKey) return "api";
      if (this.selectedNodeKey === this.fileNodeKey) return "file";
      return null;
    },
    stagingStatusScope() {
      if (this.selectedNodeKey === this.dataEnrichmentNodeKey) {
        return DATA_ENRICHMENT_STATUS_SCOPE;
      }
      if (this.selectedNodeKey === this.crmWriteNodeKey) {
        return CRM_WRITE_STATUS_SCOPE;
      }
      if (this.selectedNodeKey === this.leadBoardNodeKey) {
        return LEAD_BOARD_STATUS_SCOPE;
      }
      return [];
    },
    enrichmentScopedRecords() {
      const allowed = new Set(DATA_ENRICHMENT_STATUS_SCOPE);
      return this.stagingRecords.filter((record) => allowed.has(record.status));
    },
    enrichmentBatchRecordCount() {
      if (this.selectedNodeKey !== this.dataEnrichmentNodeKey) return 0;
      const runnable = this.enrichmentScopedRecords.filter((record) => record.status === "pending_enrichment");
      if (this.stagingActiveFilter === "all") return runnable.length;
      if (this.stagingActiveFilter !== "pending_enrichment") return 0;
      return this.enrichmentScopedRecords.filter((record) => record.status === this.stagingActiveFilter).length;
    },
    crmWriteScopedRecords() {
      const allowed = new Set(CRM_WRITE_STATUS_SCOPE);
      return this.stagingRecords.filter((record) => allowed.has(record.status));
    },
    crmWriteApprovedRecordIds() {
      if (this.selectedNodeKey !== this.crmWriteNodeKey) return [];
      if (!["all", "approved"].includes(this.stagingActiveFilter)) return [];
      return this.crmWriteScopedRecords
        .filter((record) => record.status === "approved")
        .map((record) => record.id)
        .filter(Boolean);
    },
    leadBoardScopedRecords() {
      const allowed = new Set(LEAD_BOARD_STATUS_SCOPE);
      return this.stagingRecords.filter((record) => allowed.has(record.status));
    },
    leadBoardWritableRecordIds() {
      if (this.selectedNodeKey !== this.leadBoardNodeKey) return [];
      if (!["all", "written_new_company"].includes(this.stagingActiveFilter)) return [];
      return this.leadBoardScopedRecords
        .filter((record) => record.status === "written_new_company")
        .map((record) => record.id)
        .filter(Boolean);
    },
    rowSecondaryActionLabel() {
      if (this.selectedNodeKey === this.dataEnrichmentNodeKey) {
        return this.$t("ncrm.workflows.staging.actions.runEnrichmentRow");
      }
      if (this.selectedNodeKey === this.crmWriteNodeKey) {
        return this.$t("ncrm.workflows.staging.actions.writeToCrm");
      }
      if (this.selectedNodeKey === this.leadBoardNodeKey) {
        return this.$t("ncrm.workflows.staging.actions.leadBoardAddRow");
      }
      return "";
    },
    rowSecondaryActionEnabledStatuses() {
      if (this.selectedNodeKey === this.dataEnrichmentNodeKey) return ["pending_enrichment"];
      if (this.selectedNodeKey === this.crmWriteNodeKey) return ["approved"];
      if (this.selectedNodeKey === this.leadBoardNodeKey) return ["written_new_company"];
      return [];
    },
    rowSecondaryActionDisabled() {
      if (this.selectedNodeKey === this.dataEnrichmentNodeKey) return this.loadingStagingEnrichment;
      if (this.selectedNodeKey === this.crmWriteNodeKey) return this.loadingStagingWrite;
      if (this.selectedNodeKey === this.leadBoardNodeKey) return this.loadingStagingLeadBoard || !this.leadBoardSettings.boardId || !this.leadBoardSettings.stageId;
      return false;
    },
    rowSecondaryActionColor() {
      if (this.selectedNodeKey === this.crmWriteNodeKey) return "success";
      if (this.selectedNodeKey === this.leadBoardNodeKey) return "success";
      return "info";
    },
    stagingTableTitle() {
      if (this.selectedNodeKey === this.duplicateNodeKey) {
        return this.$t("ncrm.workflows.staging.dedupTableTitle");
      }
      if (this.selectedNodeKey === this.dataEnrichmentNodeKey) {
        return this.$t("ncrm.workflows.staging.enrichmentTableTitle");
      }
      if (this.selectedNodeKey === this.crmWriteNodeKey) {
        return this.$t("ncrm.workflows.staging.crmWriteTableTitle");
      }
      if (this.selectedNodeKey === this.leadBoardNodeKey) {
        return this.$t("ncrm.workflows.staging.leadBoardTableTitle");
      }
      return this.$t("ncrm.workflows.staging.tableTitle");
    },
    stagingTableSubtitle() {
      if (this.selectedNodeKey === this.duplicateNodeKey) {
        return this.lastAutoDedupAt
          ? this.$t("ncrm.workflows.staging.dedupTableSubtitleLastRun", { time: this.formatTime(this.lastAutoDedupAt) })
          : this.$t("ncrm.workflows.staging.dedupTableSubtitle");
      }
      if (this.selectedNodeKey === this.dataEnrichmentNodeKey) {
        return this.$t("ncrm.workflows.staging.enrichmentTableSubtitle", {
          count: this.enrichmentScopedRecords.length,
        });
      }
      if (this.selectedNodeKey === this.crmWriteNodeKey) {
        return this.$t("ncrm.workflows.staging.crmWriteTableSubtitle", {
          count: this.crmWriteScopedRecords.length,
        });
      }
      if (this.selectedNodeKey === this.leadBoardNodeKey) {
        return this.$t("ncrm.workflows.staging.leadBoardTableSubtitle", {
          count: this.leadBoardScopedRecords.length,
        });
      }
      return this.lastAutoSyncAt
        ? this.$t("ncrm.workflows.staging.tableSubtitleLastSync", { time: this.formatTime(this.lastAutoSyncAt) })
        : this.$t("ncrm.workflows.staging.tableSubtitle");
    },
    emptyText() {
      if (this.selectedNodeKey === this.apiNodeKey && !this.apiSummary.endpoint) {
        return this.$t("ncrm.workflows.events.noApiSelected");
      }
      const keyByNode = {
        [this.externalFormNodeKey]: "emptyExternalForms",
        [this.apiNodeKey]: "emptyApi",
        [this.duplicateNodeKey]: "emptyDedup",
        [this.dataEnrichmentNodeKey]: "emptyEnrichment",
        [this.crmWriteNodeKey]: "emptyCrmWrite",
        [this.leadBoardNodeKey]: "emptyLeadBoard",
      };
      return this.$t(`ncrm.workflows.events.${keyByNode[this.selectedNodeKey] || "emptyFuture"}`);
    },
  },
  methods: {
    resultLabel(result) {
      return this.$t(`ncrm.workflows.events.results.${result || "stored"}`);
    },
    statusMeta(status) {
      return resolveExternalApiStatusMeta(status);
    },
    formatTime(date) {
      if (!date) return "";
      return formatDisplayTime(date, { seconds: true, fallback: "" });
    },
    handleDeliveryRowClick(_, row) {
      const deliveryId = row?.item?.id;
      if (!deliveryId || !this.usesDeliveryEvents) return;
      this.$emit("open-delivery", deliveryId);
    },
    handleRowSecondaryAction(record) {
      if (this.selectedNodeKey === this.dataEnrichmentNodeKey) {
        this.$emit("run-enrichment-record", record);
        return;
      }
      if (this.selectedNodeKey === this.crmWriteNodeKey) {
        this.$emit("write-staging-record", record);
        return;
      }
      if (this.selectedNodeKey === this.leadBoardNodeKey) {
        this.$emit("lead-board-add-record", record);
      }
    },
  },
};
</script>

<style src="./NcrmWorkflowDetail.css" scoped></style>
